import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getLegalLinks } from '~/config/legal.config';
import styles from './ConsentBanner.module.css';

export type ConsentDraft = { ads: boolean; product: boolean };

type Props = {
    /**
     * `choice` : on demande (nouveaux comptes). `notice` : on informe (comptes d'avant le bandeau, accord
     * réputé acquis) — 2 boutons : « Modifier mes choix » et « J'ai compris ».
     */
    variant?: 'choice' | 'notice';
    initial?: ConsentDraft;
    startCustomizing?: boolean;
    onDone: (choice: ConsentDraft) => void;
};

/**
 * Bandeau « Vos choix de cookies ». Refuser est aussi simple et aussi visible qu'accepter
 * (exigence CNIL) : trois boutons de même taille, aucun pré-cochage dans « Personnaliser ».
 */
export function ConsentBanner({ variant = 'choice', initial, startCustomizing = false, onDone }: Props) {
    const { t, i18n } = useTranslation();
    const privacyUrl = getLegalLinks(i18n.language).privacy;
    const [customizing, setCustomizing] = useState(startCustomizing);
    const [product, setProduct] = useState(initial?.product ?? false);
    const [ads, setAds] = useState(initial?.ads ?? false);
    const notice = variant === 'notice' && !customizing;

    return (
        <div className={styles.wrap} role="dialog" aria-labelledby="ub-consent-title" aria-live="polite">
            <div className={styles.card}>
                <h2 id="ub-consent-title" className={styles.title}>
                    {t(notice ? 'consent.noticeTitle' : 'consent.title')}
                </h2>
                <p className={styles.body}>
                    <Trans
                        i18nKey={notice ? 'consent.noticeBody' : 'consent.body'}
                        components={{
                            link: (
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                <a
                                    className={styles.link}
                                    href={privacyUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={t('consent.privacy')}
                                />
                            ),
                        }}
                    />
                </p>

                {customizing && (
                    <div className={styles.purposes}>
                        <label className={styles.purpose}>
                            <input
                                type="checkbox"
                                className={styles.switch}
                                checked={product}
                                aria-label={t('consent.productTitle')}
                                onChange={(e) => setProduct(e.target.checked)}
                            />
                            <span>
                                <strong className={styles.purposeTitle}>{t('consent.productTitle')}</strong>
                                <span className={styles.purposeText}>{t('consent.productText')}</span>
                            </span>
                        </label>
                        <label className={styles.purpose}>
                            <input
                                type="checkbox"
                                className={styles.switch}
                                checked={ads}
                                aria-label={t('consent.adsTitle')}
                                onChange={(e) => setAds(e.target.checked)}
                            />
                            <span>
                                <strong className={styles.purposeTitle}>{t('consent.adsTitle')}</strong>
                                <span className={styles.purposeText}>{t('consent.adsText')}</span>
                            </span>
                        </label>
                    </div>
                )}

                {notice ? (
                    // Comptes d'avant le bandeau (décision Victorien 20/10) : 2 boutons ; refuser passe par
                    // « Modifier mes choix » (Tout refuser y est en 1er) ou par Mon compte.
                    <div className={`${styles.actions} ${styles.actionsTwo}`}>
                        <button type="button" className={styles.button} onClick={() => setCustomizing(true)}>
                            {t('consent.change')}
                        </button>
                        <button
                            type="button"
                            className={styles.button}
                            onClick={() => onDone({ ads: true, product: true })}
                        >
                            {t('consent.understood')}
                        </button>
                    </div>
                ) : (
                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={styles.button}
                            onClick={() => onDone({ ads: false, product: false })}
                        >
                            {t('consent.refuseAll')}
                        </button>
                        {customizing ? (
                            <button type="button" className={styles.button} onClick={() => onDone({ ads, product })}>
                                {t('consent.save')}
                            </button>
                        ) : (
                            <button type="button" className={styles.button} onClick={() => setCustomizing(true)}>
                                {t('consent.customize')}
                            </button>
                        )}
                        <button
                            type="button"
                            className={styles.button}
                            onClick={() => onDone({ ads: true, product: true })}
                        >
                            {t('consent.acceptAll')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
